/* /performance — goals and AI review draft. */
import "./pay.css";
import { useState } from "react";
import { useGoals } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { AiNote, Avatar, Bar, Button, Card, EmptyState, ErrorBox, IconButton, Kpi, PageHeader, Seg, Skeleton, useToast } from "../../ui";
import { unknownEmp, useEmpMap } from "./components/common";

export default function Performance() {
  const { L, name } = useI18n();
  const toast = useToast();
  const q = useGoals();
  const emps = useEmpMap();
  const [filter, setFilter] = useState<"all" | "risk" | "track">("all");
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Skeleton />;
  const all = q.data;
  const list = all.filter((g) => filter === "all" || (filter === "risk" ? g.progress < 50 : g.progress >= 50)).sort((a, b) => a.progress - b.progress);
  const avg = all.length ? Math.round(all.reduce((a, g) => a + g.progress, 0) / all.length) : 0;
  return (
    <>
      <PageHeader title={L("Performance", "الأداء")} sub={L("Annual review 2026 · goals, check-ins and reviews", "التقييم السنوي 2026 · الأهداف والمتابعات والتقييمات")}
        actions={<Button variant="pri" icon="target" onClick={() => toast(L("Review cycle scheduled", "تمت جدولة دورة التقييم"))} data-testid="btn-review-cycle">{L("Start review cycle", "بدء دورة تقييم")}</Button>} />
      <div className="g g-3 mb">
        <Kpi testId="kpi-goals" label={L("Goals", "الأهداف")} value={String(all.length)} icon="target" />
        <Kpi testId="kpi-avg" label={L("Average progress", "متوسط التقدم")} value={String(avg)} unit="%" icon="chart" tone="info" />
        <Kpi testId="kpi-risk" label={L("At risk (< 50%)", "متعثرة (< 50%)")} value={String(all.filter((g) => g.progress < 50).length)} icon="alert" tone="warn" />
      </div>
      <div className="g g-2-1">
        <Card title={L("Goals", "الأهداف")} actions={<Seg label={L("Filter", "تصفية")} value={filter} onChange={setFilter} items={[["all", L("All", "الكل")], ["risk", L("At risk", "متعثرة")], ["track", L("On track", "على المسار")]]} />} testId="goals">
          {list.length === 0 ? <EmptyState title={L("No goals", "لا توجد أهداف")} /> : list.map((g) => {
            const e = emps.map.get(g.employeeId) ?? unknownEmp(g.employeeId);
            return (
              <div className="list-row" key={g.id} data-testid={"goal-" + g.id}>
                <Avatar name={e.nameEn} size="sm" />
                <div className="grow">
                  <div className="sm"><b>{g.title}</b></div>
                  <div className="xs muted">{name(e)}</div>
                  <div style={{ marginTop: 6 }}><Bar value={g.progress} max={100} tone={g.progress < 50 ? "warn" : ""} /></div>
                </div>
                <span className="num sm">{g.progress}%</span>
              </div>
            );
          })}
        </Card>
        <Card title={L("AI review draft", "مسودة تقييم بالذكاء")} actions={<IconButton icon="refresh" label={L("Regenerate", "إعادة الإنشاء")} onClick={() => toast(L("Regenerated", "تمت إعادة الإنشاء"))} data-testid="btn-regenerate" />}>
          <AiNote>{L("Drafted from goals, check-ins and peer feedback for Mohammed Al-Zahrani. Edit before sharing.", "مسودة من الأهداف والمتابعات وملاحظات الزملاء لمحمد الزهراني. عدّلها قبل المشاركة.")}</AiNote>
          <p className="sm" style={{ margin: "12px 0 0", lineHeight: 1.7 }}>{L("Mohammed closed 9 of 12 month-ends within five working days (target: all), improving from 6 last year. Peers highlight his VAT reconciliation work. Development area: delegate routine postings to free time for analysis.", "أنهى محمد 9 من 12 إقفالًا شهريًا خلال خمسة أيام عمل، مقارنة بـ 6 العام الماضي. يشيد زملاؤه بعمله في تسوية الضريبة. مجال التطوير: تفويض القيود الروتينية.")}</p>
        </Card>
      </div>
    </>
  );
}
